import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useWorkspace } from "@/hooks/use-workspace";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Plus, MessageSquare, Bug, Zap, AlertTriangle, Clock, CheckCircle, XCircle, Upload, Paperclip, X, Rocket } from "lucide-react";
import type { BetaFeedback, FeedbackAttachment } from "@shared/schema";
import { getEnhancementIcon, type UpcomingEnhancement } from "@/lib/enhancements-data";

const feedbackTypeConfig = {
  enhancement: { label: "Enhancement", icon: Zap, color: "bg-blue-500/10 text-blue-700 border-blue-200" },
  bug: { label: "Bug Report", icon: Bug, color: "bg-yellow-500/10 text-yellow-700 border-yellow-200" },
  critical: { label: "Critical Issue", icon: AlertTriangle, color: "bg-red-500/10 text-red-700 border-red-200" },
};

const statusConfig = {
  new: { label: "New", icon: Clock, color: "bg-gray-500/10 text-gray-700" },
  acknowledged: { label: "Acknowledged", icon: MessageSquare, color: "bg-blue-500/10 text-blue-700" },
  in_progress: { label: "In Progress", icon: Clock, color: "bg-yellow-500/10 text-yellow-700" },
  resolved: { label: "Resolved", icon: CheckCircle, color: "bg-green-500/10 text-green-700" },
  wont_fix: { label: "Won't Fix", icon: XCircle, color: "bg-gray-500/10 text-gray-500" },
};

const priorityConfig = {
  low: { label: "Low", color: "bg-gray-500/10 text-gray-700" },
  medium: { label: "Medium", color: "bg-blue-500/10 text-blue-700" },
  high: { label: "High", color: "bg-orange-500/10 text-orange-700" },
  critical: { label: "Critical", color: "bg-red-500/10 text-red-700" },
};

const urgencyConfig: Record<"low" | "medium" | "high", { label: string; color: string }> = {
  low: { label: "Low Urgency", color: "bg-gray-500/10 text-gray-700" },
  medium: { label: "Medium Urgency", color: "bg-blue-500/10 text-blue-700" },
  high: { label: "High Urgency", color: "bg-orange-500/10 text-orange-700" },
};

// Match the server-side caps in POST /api/feedback so client-side validation
// short-circuits before we PUT a base64 payload that the server would reject.
const PER_FILE_LIMIT = 3 * 1024 * 1024;
const TOTAL_LIMIT = 7 * 1024 * 1024;
const MAX_FILES = 5;
const ALLOWED_MIME = new Set([
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/gif",
  "image/webp",
  "image/heic",
  "image/heif",
  "application/pdf",
  "text/plain",
]);

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

async function fileToAttachment(file: File): Promise<FeedbackAttachment> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  // Build base64 in chunks to avoid stack overflow on large files. Mirrors the
  // inbox-panel reply attachment helper.
  let bin = "";
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    bin += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)));
  }
  const contentBase64 = btoa(bin);
  return {
    filename: file.name,
    contentType: file.type || "application/octet-stream",
    contentBase64,
    size: file.size,
  };
}

export default function FeedbackPage() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState<{
    feedbackType: string;
    title: string;
    description: string;
    urgency: "" | "low" | "medium" | "high";
  }>({
    feedbackType: "",
    title: "",
    description: "",
    urgency: "",
  });
  const [attachments, setAttachments] = useState<FeedbackAttachment[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Use buildPath so the request always carries the workspace slug in the URL.
  // Bare /api/feedback depends on loadPermissionContext resolving the workspace
  // from the session — if that falls back to null, the route returns 400.
  const { buildPath } = useWorkspace();

  const { data: feedback = [], isLoading } = useQuery<BetaFeedback[]>({
    queryKey: [buildPath("/api/feedback")],
  });

  const submitFeedback = useMutation({
    mutationFn: async (data: {
      feedbackType: string;
      title: string;
      description: string;
      urgency: "" | "low" | "medium" | "high";
      attachments: FeedbackAttachment[];
      pageUrl?: string;
    }) => {
      return apiRequest("POST", buildPath("/api/feedback"), {
        feedbackType: data.feedbackType,
        title: data.title,
        description: data.description,
        urgency: data.urgency || undefined,
        attachments: data.attachments,
        pageUrl: window.location.href,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [buildPath("/api/feedback")] });
      toast({ title: "Feedback submitted", description: "Thank you for your feedback!" });
      setDialogOpen(false);
      setFormData({ feedbackType: "", title: "", description: "", urgency: "" });
      setAttachments([]);
    },
    onError: (err: any) => {
      // Surface the server's validation message when present so size/format errors
      // are actionable instead of "Failed to submit feedback. Please try again."
      let msg = err?.message || "Failed to submit feedback. Please try again.";
      const m = msg.match(/^\d+:\s*(.+)$/);
      if (m) {
        try {
          const parsed = JSON.parse(m[1]);
          if (parsed?.message) msg = parsed.message;
        } catch {
          msg = m[1];
        }
      }
      toast({ title: "Error", description: msg, variant: "destructive" });
    },
  });

  const addFiles = async (files: File[]) => {
    if (files.length === 0) return;
    let runningTotal = attachments.reduce((s, a) => s + a.size, 0);
    let remainingSlots = MAX_FILES - attachments.length;
    if (remainingSlots <= 0) {
      toast({
        title: "Too many attachments",
        description: `You can attach at most ${MAX_FILES} files per submission.`,
        variant: "destructive",
      });
      return;
    }
    const next: FeedbackAttachment[] = [];
    for (const f of files) {
      if (remainingSlots <= 0) {
        toast({
          title: "Too many attachments",
          description: `You can attach at most ${MAX_FILES} files per submission.`,
          variant: "destructive",
        });
        break;
      }
      if (f.type && !ALLOWED_MIME.has(f.type.toLowerCase())) {
        toast({
          title: "Unsupported file type",
          description: `"${f.name}" is not a supported file type. Allowed: PNG, JPEG, GIF, WebP, HEIC, PDF, TXT.`,
          variant: "destructive",
        });
        continue;
      }
      if (f.size > PER_FILE_LIMIT) {
        toast({
          title: "Attachment too large",
          description: `"${f.name}" exceeds the 3MB per-file limit.`,
          variant: "destructive",
        });
        continue;
      }
      if (runningTotal + f.size > TOTAL_LIMIT) {
        toast({
          title: "Attachments too large",
          description: "Total attachments must stay under 7MB.",
          variant: "destructive",
        });
        break;
      }
      try {
        const att = await fileToAttachment(f);
        next.push(att);
        runningTotal += f.size;
        remainingSlots -= 1;
      } catch {
        toast({
          title: "Could not read file",
          description: `Failed to read "${f.name}".`,
          variant: "destructive",
        });
      }
    }
    if (next.length > 0) {
      setAttachments((prev) => [...prev, ...next]);
    }
  };

  const handleFileInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    await addFiles(files);
    // Reset the native input so picking the same file twice still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types?.includes("Files")) {
      setIsDragging(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types?.includes("Files")) {
      e.dataTransfer.dropEffect = "copy";
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear when actually leaving the dropzone (not bubbling from a child).
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    await addFiles(files);
  };

  const removeAttachment = (idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = () => {
    if (!formData.feedbackType || !formData.title || !formData.description) {
      toast({ title: "Missing fields", description: "Please fill in all required fields.", variant: "destructive" });
      return;
    }
    submitFeedback.mutate({ ...formData, attachments });
  };

  const formatDate = (date: Date | string | null) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const { data: upcomingData } = useQuery<{ features: UpcomingEnhancement[] }>({
    queryKey: ["/api/enhancements/upcoming"],
  });

  const upcomingFeatures = upcomingData?.features ?? [];

  return (
    <div className="flex-1 overflow-auto p-3 sm:p-6">
      <div className="max-w-6xl mx-auto flex gap-6 items-start">
        {/* ── Left: feedback list ─────────────────────────────── */}
        <div className="flex-1 min-w-0 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" data-testid="text-page-title">BetaTest Feedback</h1>
            <p className="text-muted-foreground">
              Share your ideas, report bugs, or flag critical issues
            </p>
          </div>
          <Dialog
            open={dialogOpen}
            onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                // Reset transient state when the dialog closes via Cancel / Esc / outside click.
                setAttachments([]);
                setIsDragging(false);
              }
            }}
          >
            <DialogTrigger asChild>
              <Button data-testid="button-new-feedback">
                <Plus className="w-4 h-4 mr-2" />
                New Feedback
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Submit Feedback</DialogTitle>
                <DialogDescription>
                  Help us improve Clarik by sharing your thoughts
                </DialogDescription>
              </DialogHeader>
              <p
                className="text-sm font-bold text-foreground"
                data-testid="text-feedback-screenshot-helper"
              >
                Please provide screenshots of all bugs and critical issues.
              </p>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="feedbackType">Type *</Label>
                  <Select
                    value={formData.feedbackType}
                    onValueChange={(value) => setFormData({ ...formData, feedbackType: value })}
                  >
                    <SelectTrigger data-testid="select-feedback-type">
                      <SelectValue placeholder="Select feedback type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="enhancement">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-blue-500" />
                          Enhancement Request
                        </div>
                      </SelectItem>
                      <SelectItem value="bug">
                        <div className="flex items-center gap-2">
                          <Bug className="w-4 h-4 text-yellow-500" />
                          Bug Report
                        </div>
                      </SelectItem>
                      <SelectItem value="critical">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-500" />
                          Critical Issue
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="urgency">Urgency</Label>
                  <Select
                    value={formData.urgency}
                    onValueChange={(value) =>
                      setFormData({ ...formData, urgency: value as "low" | "medium" | "high" })
                    }
                  >
                    <SelectTrigger data-testid="select-feedback-urgency">
                      <SelectValue placeholder="Select urgency level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low" data-testid="option-urgency-low">Low</SelectItem>
                      <SelectItem value="medium" data-testid="option-urgency-medium">Medium</SelectItem>
                      <SelectItem value="high" data-testid="option-urgency-high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    placeholder="Brief summary of your feedback"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    data-testid="input-feedback-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    placeholder="Provide details about your feedback. For bugs, include steps to reproduce."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={5}
                    data-testid="input-feedback-description"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Attachments</Label>
                  <div
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        fileInputRef.current?.click();
                      }
                    }}
                    className={[
                      "flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed p-6 cursor-pointer transition-colors",
                      isDragging
                        ? "border-primary bg-primary/5"
                        : "border-muted-foreground/25 hover:border-muted-foreground/50 hover:bg-muted/30",
                    ].join(" ")}
                    data-testid="dropzone-feedback-attachments"
                  >
                    <Upload className="w-6 h-6 text-muted-foreground" aria-hidden="true" />
                    <div className="text-sm text-center">
                      <span className="font-medium text-foreground">Click to upload</span>
                      <span className="text-muted-foreground"> or drag and drop</span>
                    </div>
                    <p className="text-xs text-muted-foreground text-center">
                      Up to {MAX_FILES} files, 3MB each (7MB total). Screenshots welcome.
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*,application/pdf,.txt,.log,.csv,.json"
                      className="hidden"
                      onChange={handleFileInputChange}
                      data-testid="input-feedback-attachments"
                    />
                  </div>
                  {attachments.length > 0 && (
                    <ul className="space-y-1.5" data-testid="list-feedback-attachments">
                      {attachments.map((a, i) => (
                        <li
                          key={`${a.filename}-${i}`}
                          className="flex items-center justify-between gap-2 rounded-md border bg-muted/30 px-3 py-2"
                          data-testid={`row-feedback-attachment-${i}`}
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            <Paperclip className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
                            <span
                              className="text-sm truncate"
                              data-testid={`text-feedback-attachment-name-${i}`}
                              title={a.filename}
                            >
                              {a.filename}
                            </span>
                            <span
                              className="text-xs text-muted-foreground shrink-0"
                              data-testid={`text-feedback-attachment-size-${i}`}
                            >
                              {formatBytes(a.size)}
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={() => removeAttachment(i)}
                            aria-label={`Remove ${a.filename}`}
                            data-testid={`button-remove-feedback-attachment-${i}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  data-testid="button-cancel-feedback"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSubmit} 
                  disabled={submitFeedback.isPending}
                  data-testid="button-submit-feedback"
                >
                  {submitFeedback.isPending ? "Submitting..." : "Submit Feedback"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : feedback.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 text-center">
              <MessageSquare className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="font-semibold text-lg">No feedback yet</h3>
              <p className="text-muted-foreground max-w-sm mt-2">
                You haven't submitted any feedback. Click "New Feedback" to share your first idea or report an issue.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {feedback.map((item) => {
              const typeConfig = feedbackTypeConfig[item.feedbackType as keyof typeof feedbackTypeConfig] || feedbackTypeConfig.enhancement;
              const statusCfg = statusConfig[item.status as keyof typeof statusConfig] || statusConfig.new;
              const priorityCfg = item.priority ? priorityConfig[item.priority as keyof typeof priorityConfig] : null;
              const urgencyCfg = item.urgency ? urgencyConfig[item.urgency as "low" | "medium" | "high"] : null;
              const TypeIcon = typeConfig.icon;
              const StatusIcon = statusCfg.icon;
              const itemAttachments = (item.attachments || []) as FeedbackAttachment[];

              return (
                <Card key={item.id} data-testid={`card-feedback-${item.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge variant="outline" className={typeConfig.color}>
                            <TypeIcon className="w-3 h-3 mr-1" />
                            {typeConfig.label}
                          </Badge>
                          <Badge variant="outline" className={statusCfg.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusCfg.label}
                          </Badge>
                          {urgencyCfg && (
                            <Badge
                              variant="outline"
                              className={urgencyCfg.color}
                              data-testid={`badge-feedback-urgency-${item.id}`}
                            >
                              {urgencyCfg.label}
                            </Badge>
                          )}
                          {priorityCfg && (
                            <Badge variant="outline" className={priorityCfg.color}>
                              {priorityCfg.label} Priority
                            </Badge>
                          )}
                          {itemAttachments.length > 0 && (
                            <Badge
                              variant="outline"
                              className="bg-gray-500/10 text-gray-700"
                              data-testid={`badge-feedback-attachments-${item.id}`}
                            >
                              <Paperclip className="w-3 h-3 mr-1" />
                              {itemAttachments.length} {itemAttachments.length === 1 ? "attachment" : "attachments"}
                            </Badge>
                          )}
                        </div>
                        <CardTitle className="text-lg">{item.title}</CardTitle>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDate(item.createdAt)}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                      {item.description}
                    </p>
                    {itemAttachments.length > 0 && (
                      <ul
                        className="mt-3 space-y-1"
                        data-testid={`list-feedback-card-attachments-${item.id}`}
                      >
                        {itemAttachments.map((a, i) => {
                          const isImage = a.contentType?.startsWith("image/");
                          const dataUrl = `data:${a.contentType};base64,${a.contentBase64}`;
                          return (
                            <li
                              key={`${a.filename}-${i}`}
                              className="flex items-center gap-2 text-xs text-muted-foreground"
                              data-testid={`row-feedback-card-attachment-${item.id}-${i}`}
                            >
                              <Paperclip className="w-3 h-3 shrink-0" aria-hidden="true" />
                              <a
                                href={dataUrl}
                                target="_blank"
                                rel="noreferrer"
                                download={isImage ? undefined : a.filename}
                                className="underline truncate"
                                title={a.filename}
                              >
                                {a.filename}
                              </a>
                              <span className="shrink-0">{formatBytes(a.size)}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                    {item.adminNotes && (
                      <div className="mt-4 p-3 bg-muted rounded-md">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Admin Response:</p>
                        <p className="text-sm">{item.adminNotes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        </div>
        {/* ── Right: Upcoming panel ─────────────────────────── */}
        <div className="w-64 shrink-0 hidden lg:block" data-testid="panel-upcoming">
          <div className="sticky top-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                  <Rocket className="w-4 h-4 text-primary" />
                  Upcoming
                </CardTitle>
                <CardDescription className="text-xs">
                  Features on the horizon
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3.5">
                {upcomingFeatures.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nothing on the horizon yet — check back soon.</p>
                ) : (
                  upcomingFeatures.map((f) => {
                    const Icon = getEnhancementIcon(f.iconKey);
                    const shortDesc = f.description.length > 90
                      ? f.description.slice(0, 90).trimEnd() + "…"
                      : f.description;
                    return (
                      <div key={f.id} className="flex items-start gap-2.5" data-testid={`upcoming-feature-${f.id}`}>
                        <div className="mt-0.5 shrink-0 rounded-md bg-primary/10 p-1">
                          <Icon className="w-3 h-3 text-primary" />
                        </div>
                        <div>
                          <p className="text-xs font-medium leading-snug">{f.name}</p>
                          <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">{shortDesc}</p>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
